/**
 * Performance Optimization Utilities
 * 
 * This file contains utility functions to improve performance across the application,
 * particularly for the ExpandedProductCard component.
 */

/**
 * Schedules high-priority work using requestAnimationFrame with a requestIdleCallback fallback
 * This ensures that rendering tasks happen at the optimal time in the browser's rendering cycle
 */
export const scheduleHighPriorityUpdate = (callback) => {
  // Check if requestIdleCallback is available (not in Safari)
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    // Schedule with idle callback but with a timeout to ensure it runs
    return window.requestIdleCallback(callback, { timeout: 100 });
  } else if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
    // Fallback to requestAnimationFrame which is widely supported
    return window.requestAnimationFrame(callback);
  } else {
    // Ultimate fallback to setTimeout with a small delay
    return setTimeout(callback, 16); // ~ 60fps
  }
};

/**
 * Schedules low-priority work using requestIdleCallback or setTimeout as a fallback
 * This is ideal for non-critical UI updates or background processing
 */
export const scheduleLowPriorityUpdate = (callback) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(callback);
  } else {
    return setTimeout(callback, 200); // Low priority can wait
  }
};

/**
 * Cancels a scheduled update based on the type of scheduler used
 */
export const cancelScheduledUpdate = (id, priority = 'high') => {
  if (typeof window === 'undefined') return;
  
  if (priority === 'high') {
    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(id);
    } else if ('cancelAnimationFrame' in window) {
      window.cancelAnimationFrame(id);
    } else {
      clearTimeout(id);
    }
  } else {
    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(id);
    } else {
      clearTimeout(id);
    }
  }
};

/**
 * Triggers browser layout calculation to avoid layout thrashing
 * by batching read and write operations
 */
export const forceLayout = (element) => {
  if (!element) return;
  
  // Force a browser layout calculation
  void element.offsetHeight;
};

/**
 * Preloads an image to ensure it's in the browser cache when needed
 */
export const preloadImage = (src) => {
  if (!src || typeof window === 'undefined') return;
  
  const img = new Image();
  img.src = src;
};

/**
 * Determines if the device is likely a low-end device based on hardware concurrency
 * This allows us to adapt performance strategies based on device capabilities
 */
export const isLowEndDevice = () => {
  if (typeof window === 'undefined' || !navigator.hardwareConcurrency) {
    return false;
  }
  
  return navigator.hardwareConcurrency <= 4;
};

/**
 * Apply performance optimizations to a DOM element
 */
export const optimizeElement = (element) => {
  if (!element) return;
  
  // Apply performance hints to the element
  element.style.willChange = 'transform, opacity';
  element.style.transform = 'translateZ(0)';
  element.style.contain = 'content';
};

/**
 * Remove performance optimizations from a DOM element when no longer needed
 * to free up resources
 */
export const cleanupElementOptimizations = (element) => {
  if (!element) return;
  
  // Remove performance hints
  element.style.willChange = 'auto';
  element.style.transform = '';
  element.style.contain = '';
};

/**
 * Make an element focusable and prepare it for focus management
 * Useful for returning focus to non-interactive elements
 */
export const prepareElementForFocus = (element) => {
  if (!element) return;
  
  // Ensure the element is focusable
  element.setAttribute('tabindex', '-1');
  
  // Add appropriate ARIA attributes to indicate it can receive focus
  if (!element.hasAttribute('role')) {
    element.setAttribute('role', 'group');
  }
  
  // Ensure it has appropriate styling for focus indication
  const currentStyle = window.getComputedStyle(element);
  if (currentStyle.outline === 'none' || currentStyle.outlineWidth === '0px') {
    // Store original outline for restoration
    element.dataset.originalOutline = currentStyle.outline;
    // Add a subtle focus outline that will only show when focused
    element.style.outline = 'none';
    element.addEventListener('focus', () => {
      element.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.4)';
    }, { once: false });
    element.addEventListener('blur', () => {
      element.style.boxShadow = '';
    }, { once: false });
  }
  
  return element;
};

/**
 * Check if an element is completely visible in the viewport
 * Helps determine if we need to scroll to make it fully visible
 * @param {HTMLElement} element - The element to check
 * @param {number} padding - Optional padding to ensure element has space around it (default: 20px)
 * @returns {boolean} - Whether the element is fully visible
 */
export const isElementInViewport = (element, padding = 20) => {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  
  // Check if the element is fully visible with padding
  return (
    rect.top >= padding &&
    rect.left >= padding &&
    rect.bottom <= (windowHeight - padding) &&
    rect.right <= (windowWidth - padding)
  );
};
